import Highcharts from "highcharts";
import { MeetingNotesSummary, SentimentMovingAveragePoint } from "./types";
import { ALL_CATEGORIES_VALUE } from "./components/MeetingHistorySection";
import { useEffect, useRef, useMemo } from "react";
import "highcharts/modules/exporting";
import "highcharts/modules/accessibility";

// Configure Highcharts license
if (process.env.REACT_APP_HIGHCHARTS_LICENSE_KEY) {
    // @ts-ignore - Highcharts license key assignment
    Highcharts.licenseKey = process.env.REACT_APP_HIGHCHARTS_LICENSE_KEY;
}

export const useChartData = (
    meetingNotes: MeetingNotesSummary[],
    selectedCategory: string,
    allAvailableCategories: string[]
) => {
    return useMemo(() => {
        try {
            if (!meetingNotes || meetingNotes.length === 0) return null;

            if (selectedCategory === ALL_CATEGORIES_VALUE) {
                return prepareHighchartsData(
                    meetingNotes,
                    ALL_CATEGORIES_VALUE,
                    allAvailableCategories
                );
            } else {
                const filteredNotes = meetingNotes.filter(
                    (note) => note.category === selectedCategory
                );
                return prepareHighchartsData(
                    filteredNotes,
                    selectedCategory,
                    allAvailableCategories
                );
            }
        } catch (error) {
            console.error("Error preparing chart data:", error);
            return null;
        }
    }, [meetingNotes, selectedCategory, allAvailableCategories]);
};

export const useChart = (
    chartData: ReturnType<typeof useChartData>,
    movingAverageData: SentimentMovingAveragePoint[],
    handleMarkerClick: (point: any) => void,
    isChartVisible: boolean,
    companySectorName: string | null
) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<Highcharts.Chart | null>(null);

    useEffect(() => {
        try {
            if (!isChartVisible || !chartRef.current || !chartData) return;

            // Destroy existing chart
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }

            const chartConfig = getChartConfig(
                chartData,
                movingAverageData,
                companySectorName
            );
            if (chartConfig && Object.keys(chartConfig).length > 0) {
                // Add click handler to chart config
                chartConfig.chart = {
                    ...chartConfig.chart,
                    events: {
                        click: handleMarkerClick,
                    },
                };

                // Add click event to plot options
                if (chartConfig.plotOptions?.line?.point?.events) {
                    chartConfig.plotOptions.line.point.events.click = function (
                        this: any
                    ) {
                        handleMarkerClick(this);
                    };
                }

                chartInstance.current = Highcharts.chart(
                    chartRef.current,
                    chartConfig
                );
            }
        } catch (error) {
            console.error("Error creating chart:", error);
            // Clean up on error
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
        }

        return () => {
            try {
                if (chartInstance.current) {
                    chartInstance.current.destroy();
                    chartInstance.current = null;
                }
            } catch (error) {
                console.error("Error destroying chart:", error);
            }
        };
    }, [
        chartData,
        handleMarkerClick,
        movingAverageData,
        isChartVisible,
        companySectorName,
    ]);

    return { chartRef, chartInstance };
};

// Generate colors for different categories
const generateCategoryColors = (categories: string[]) => {
    const colorPalette = [
        "#3498db", // Blue for Quarterly/full year earnings
        "#f39c12", // Orange for Meeting with executives
        "#e74c3c", // Red for News
        "#1abc9c", // Teal for Focus list
        "#9b59b6", // Purple for Meeting with expert
        "#f1c40f", // Yellow for Investor Relations call
        "#2ecc71", // Green
        "#e67e22", // Dark orange
        "#34495e", // Dark blue-gray
        "#ff6b9d", // Pink/Magenta
        "#95a5a6", // Gray
    ];

    const categoryColors: { [key: string]: string } = {};
    categories.forEach((category, index) => {
        categoryColors[category] = colorPalette[index % colorPalette.length];
    });

    return categoryColors;
};

// Prepare chart data for Highcharts with category-based series
const prepareHighchartsData = (
    meetingNotes: MeetingNotesSummary[],
    selectedCategory: string = ALL_CATEGORIES_VALUE,
    allAvailableCategories: string[] = []
) => {
    if (!meetingNotes || meetingNotes.length === 0) {
        return null;
    }

    // Use all available categories for consistent legend, fallback to data categories
    const categoriesForLegend =
        allAvailableCategories.length > 0
            ? allAvailableCategories
            : Array.from(new Set(meetingNotes.map((note) => note.category)));

    // Generate colors for all available categories
    const categoryColors = generateCategoryColors(categoriesForLegend);

    if (selectedCategory === ALL_CATEGORIES_VALUE) {
        // For "all" category, create a single series with all points sorted by date
        // but with different colored markers for each category
        const sortedNotes = [...meetingNotes].sort(
            (a, b) =>
                new Date(a.meeting_note_date).getTime() -
                new Date(b.meeting_note_date).getTime()
        );

        const data = sortedNotes.map((note) => ({
            x: new Date(note.meeting_note_date).getTime(),
            y: note.sentiment_score,
            title: note.meeting_note_title,
            summary: note.summary_markdown,
            category: note.category,
            marker: {
                fillColor: categoryColors[note.category],
                lineColor: categoryColors[note.category],
                radius: 6,
                symbol: "circle",
                lineWidth: 0,
            },
        }));

        return {
            series: [
                {
                    type: "line",
                    name: "Meeting Sentiment",
                    data: data,
                    color: "#3498db", // Blue line color
                    lineWidth: 2,
                    marker: {
                        enabled: true,
                        radius: 6,
                        symbol: "circle",
                        lineWidth: 0,
                    },
                    connectNulls: false,
                    showInLegend: false, // Don't show main series in legend
                    allowPointSelect: false, // Disable point selection for main series
                    cursor: "pointer", // Pointer cursor for markers
                    states: {
                        hover: {
                            enabled: true, // Enable hover for main series
                        },
                        inactive: {
                            enabled: false, // Disable inactive state
                        },
                    },
                },
            ] as Highcharts.SeriesOptionsType[],
            categories: categoriesForLegend, // Use all available categories
            colors: categoryColors,
            isAllSelected: true,
        };
    } else {
        // For specific category, create separate series for each category
        const notesByCategory: { [key: string]: MeetingNotesSummary[] } = {};
        meetingNotes.forEach((note) => {
            if (!notesByCategory[note.category]) {
                notesByCategory[note.category] = [];
            }
            notesByCategory[note.category].push(note);
        });

        // Sort notes by date within each category
        Object.keys(notesByCategory).forEach((category) => {
            notesByCategory[category].sort(
                (a, b) =>
                    new Date(a.meeting_note_date).getTime() -
                    new Date(b.meeting_note_date).getTime()
            );
        });

        const categories = Object.keys(notesByCategory);

        // Create series data for each category - but keep original category colors
        const series: Highcharts.SeriesOptionsType[] = categories.map(
            (category) => {
                const data = notesByCategory[category].map((note) => ({
                    x: new Date(note.meeting_note_date).getTime(),
                    y: note.sentiment_score,
                    title: note.meeting_note_title,
                    summary: note.summary_markdown,
                    category: note.category,
                    marker: {
                        fillColor: categoryColors[note.category], // Use original category color
                        lineColor: categoryColors[note.category], // Use original category color
                        radius: 6,
                        symbol: "circle",
                        lineWidth: 0,
                    },
                }));

                return {
                    type: "line",
                    name: category,
                    data: data,
                    color: categoryColors[category], // Use original category color
                    marker: {
                        enabled: true,
                        radius: 6,
                        symbol: "circle",
                        lineWidth: 0,
                    },
                    lineWidth: 2,
                    connectNulls: false,
                    cursor: "pointer", // Pointer cursor for markers
                    allowPointSelect: false, // Disable point selection
                    showInLegend: false, // Don't show in legend to avoid duplicates
                    states: {
                        hover: {
                            enabled: true, // Enable hover for data series
                        },
                        inactive: {
                            enabled: false, // Disable inactive state
                        },
                    },
                } as Highcharts.SeriesLineOptions;
            }
        );

        return {
            series,
            categories: categoriesForLegend, // Use all available categories
            colors: categoryColors,
            isAllSelected: false,
        };
    }
};

// Highcharts configuration
const getChartConfig = (
    chartData: ReturnType<typeof prepareHighchartsData>,
    movingAverageData: SentimentMovingAveragePoint[],
    companySectorName: string | null
): Highcharts.Options => {
    if (!chartData) {
        return {};
    }

    const movingAverageSeries: Highcharts.SeriesLineOptions | null =
        movingAverageData.length > 0
            ? {
                  type: "line",
                  name: `6M moving avg (sector: ${companySectorName})`,
                  data: movingAverageData.map((d) => ({
                      x: new Date(d.date).getTime(),
                      y: d.moving_average,
                      title: "6-month moving average",
                  })),
                  dashStyle: "ShortDash",
                  lineWidth: 2,
                  color: "#ffaa00",
                  marker: { enabled: false },
                  showInLegend: true,
                  enableMouseTracking: true,
              }
            : null;

    const config: Highcharts.Options = {
        chart: {
            type: "line",
            backgroundColor: "transparent",
            height: 400,
            spacingBottom: 20,
            spacingTop: 20,
            spacingLeft: 0,
            spacingRight: 10,
            animation: true,
        },
        title: {
            text: "", // Remove title
        },
        accessibility: {
            enabled: true,
            description:
                "A line chart showing meeting sentiment scores over time with different categories",
            keyboardNavigation: {
                enabled: true,
            },
            point: {
                valueDescriptionFormat:
                    "Meeting on {point.x:%Y-%m-%d} with sentiment score of {point.y} in {point.category} category.",
            },
            series: {
                descriptionFormatter: function (series) {
                    return (
                        series.name +
                        " with " +
                        series.points.length +
                        " data points."
                    );
                },
            },
        },
        exporting: {
            enabled: false, // Disable export menu button
        },
        xAxis: {
            type: "datetime",
            title: {
                text: "Date",
                style: {
                    fontSize: "12px",
                    color: "#666",
                },
            },
            gridLineWidth: 1,
            gridLineColor: "#f0f0f0",
            lineColor: "#f0f0f0",
            tickColor: "#f0f0f0",
            minorTickColor: "#f0f0f0",
            dateTimeLabelFormats: {
                month: "%b %y", // Display short month name
            },
            tickInterval: 30 * 24 * 3600 * 1000, // Approximate milliseconds in a month
            labels: {
                formatter: function () {
                    return Highcharts.dateFormat("%b %y", this.value as number); // Display short month name
                },
                style: {
                    fontSize: "12px",
                    color: "#666",
                },
            },
        },
        yAxis: {
            title: {
                text: "Sentiment Score",
                style: {
                    fontSize: "12px",
                    color: "#666",
                },
            },
            min: -5,
            max: 5,
            tickInterval: 1,
            gridLineColor: "#f0f0f0",
            gridLineWidth: 1,
            plotLines: [
                {
                    color: "#666",
                    width: 2,
                    value: 0,
                    zIndex: 2,
                    dashStyle: "Dash",
                },
            ],
            labels: {
                style: {
                    fontSize: "11px",
                    color: "#666",
                },
            },
        },
        legend: {
            enabled: true, // Always show legend
            align: "left",
            verticalAlign: "bottom",
            layout: "horizontal",
            alignColumns: true,
            x: 0,
            y: 0,
            itemMarginTop: 2,
            itemMarginBottom: 2,
            symbolWidth: 12,
            symbolHeight: 12,
            symbolRadius: 6,
            itemStyle: {
                fontSize: "12px",
                fontWeight: "normal",
                color: "#333",
                cursor: "default", // Remove pointer cursor
            },
            itemHoverStyle: {
                fontSize: "12px",
                fontWeight: "normal",
                color: "#333", // Prevent hover style changes
            },
            itemHiddenStyle: {
                fontSize: "12px",
                fontWeight: "normal",
                color: "#333", // Prevent hidden style changes - no strike-through
                textDecoration: "none", // Explicitly prevent strike-through
            },
            events: {
                itemClick: function () {
                    return false; // Prevent legend item clicking
                },
            },
        },
        plotOptions: {
            line: {
                animation: {
                    duration: 750,
                    easing: "easeOutQuart",
                },
                marker: {
                    enabled: true,
                    radius: 6,
                    symbol: "circle",
                    lineWidth: 0,
                },
                lineWidth: 2,
                states: {
                    hover: {
                        lineWidth: 2,
                    },
                },
                connectNulls: false,
                point: {
                    events: {
                        click: function (this: any) {
                            // This will be handled in the component
                            if (this.series.chart.options.chart.events?.click) {
                                this.series.chart.options.chart.events.click.call(
                                    this,
                                    this
                                );
                            }
                        },
                    },
                },
                cursor: "pointer", // Add pointer cursor to markers
            },
        },
        tooltip: {
            useHTML: true,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderWidth: 1,
            borderColor: "#ccc",
            borderRadius: 5,
            shadow: true,
            formatter: function (this: any) {
                const point = this.point;
                const markerColor = point.marker?.fillColor || point.color;
                return `
                  <div style="padding: 8px; font-size: 12px;">
                      <strong style="color: ${markerColor};">${
                    point.title
                }</strong><br/>
                      <strong>Date:</strong> ${Highcharts.dateFormat(
                          "%d-%m-%Y",
                          point.x
                      )}<br/>
                      <strong>Sentiment Score:</strong> ${point.y}
                  </div>
              `;
            },
        },
        series: [
            // Order matters here. Series lower in this list, overlap the ones above it
            ...(movingAverageSeries ? [movingAverageSeries] : []),
            ...chartData.series,
        ],
        credits: {
            enabled: false,
        },
    };

    // Always add category markers to legend
    if (chartData.categories.length > 0) {
        // Remove the previous legend configuration since we set it above

        // Add series for legend when showing categories - always show all categories
        const legendSeries = chartData.categories.map((category) => ({
            type: "line",
            name: category,
            color: chartData.colors[category],
            visible: true, // Make visible so color shows in legend
            showInLegend: true, // Show in legend
            enableMouseTracking: false, // Disable mouse tracking for legend series
            allowPointSelect: false, // Disable point selection
            marker: {
                enabled: true,
                radius: 6,
                symbol: "circle",
                lineWidth: 0,
                fillColor: chartData.colors[category],
                lineColor: chartData.colors[category],
            },
            data: [], // Empty data so it doesn't affect the chart
            lineWidth: 0, // No line width so series doesn't appear on chart
            states: {
                hover: {
                    enabled: false, // Disable hover state
                },
                inactive: {
                    enabled: false, // Disable inactive state
                },
                select: {
                    enabled: false, // Disable select state
                },
            },
            events: {
                legendItemClick: function (e: any) {
                    e.preventDefault(); // Prevent default behavior
                    return false; // Prevent legend item clicking
                },
            },
        }));

        config.series = [
            ...(config.series || []),
            ...legendSeries,
        ] as Highcharts.SeriesOptionsType[];

        // Disable all legend interactions globally - remove itemEvents as it's not valid
        // Legend interaction is disabled through series events instead

        // Also disable legend item clicking globally in plotOptions
        config.plotOptions = {
            ...config.plotOptions,
            series: {
                ...config.plotOptions?.series,
                events: {
                    legendItemClick: function () {
                        return false; // Prevent toggling
                    },
                },
                allowPointSelect: false,
                states: {
                    hover: {
                        enabled: false,
                    },
                    inactive: {
                        enabled: false,
                    },
                },
            },
        };
    }

    return config;
};
